/**
 * Bulk site deactivation dialog — preflights selected SiteGrid rows and captures
 * the radio handling option required when active sites have attached units.
 *
 * Contains:
 * - BulkDeactivateSitesDialog — dialog component used by SiteGrid bulk actions
 */

import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePreflightDeactivateSitesMutation } from '@sophocles/features/entities/site';

export default function BulkDeactivateSitesDialog({
  open,
  onClose,
  onConfirm,
  siteRows,
  selectAll = false,
  filters = {},
  count = 0,
  isBillboard = true,
}) {
  const { t } = useTranslation();
  const [preflightDeactivateSites, { data: preflight, isLoading, isError, reset }] =
    usePreflightDeactivateSitesMutation();
  const [radioAction, setRadioAction] = useState('inventory');
  const [selectionError, setSelectionError] = useState(false);
  const wasOpenRef = useRef(false);

  const siteIds = useMemo(() => siteRows.map(row => row.id).filter(Boolean), [siteRows]);
  const siteTerm = isBillboard ? t('sites.structure', 'structure') : t('sites.siteLower', 'site');
  const siteTermPlural = isBillboard ? t('sites.structures', 'structures') : t('sites.sites', 'sites');
  const totalCount = preflight?.active_sites ?? count;
  const hasUnits = (preflight?.total_units ?? 0) > 0;
  const confirmDisabled = isLoading || isError || !preflight || (hasUnits && !radioAction);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      reset();
      setRadioAction('inventory');
      setSelectionError(false);
    }
    wasOpenRef.current = open;
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    if (!selectAll && siteIds.length === 0) return;
    preflightDeactivateSites({ siteIds, selectAll, filters });
  }, [open, selectAll, siteIds, filters, preflightDeactivateSites]);

  const handleConfirm = useCallback(() => {
    if (hasUnits && !radioAction) {
      setSelectionError(true);
      return;
    }
    onConfirm({ radio_action: radioAction || 'inventory' });
  }, [hasUnits, radioAction, onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('sites.deactivateSiteCountTitle', 'Deactivate {{count}} {{noun}}', {
          count: totalCount,
          noun: totalCount === 1 ? siteTerm : siteTermPlural,
        })}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography color="error">
            {t('sites.deactivatePreflightFailed', 'Failed to load preflight data.')}
          </Typography>
        ) : preflight ? (
          <Box>
            {!selectAll && siteRows.length > 0 && siteRows.length <= 20 ? (
              <Box sx={{ mb: 2 }}>
                {siteRows.map(row => (
                  <Box key={row.id} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.name}
                    </Typography>
                    {row.description ? (
                      <Typography variant="body2" color="text.secondary">
                        {row.description}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Box>
            ) : null}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('sites.deactivateWill', 'This action will:')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" variant="body2">
                  {t('sites.deactivateActiveSites', 'Deactivate {{count}} active {{noun}}', {
                    count: preflight.active_sites,
                    noun: preflight.active_sites === 1 ? siteTerm : siteTermPlural,
                  })}
                </Typography>
                {preflight.already_deactivated?.length > 0 ? (
                  <Typography component="li" variant="body2" color="text.secondary">
                    {t('sites.skipAlreadyDeactivated', 'Skip {{count}} already deactivated {{noun}}', {
                      count: preflight.already_deactivated.length,
                      noun: preflight.already_deactivated.length === 1 ? siteTerm : siteTermPlural,
                    })}
                  </Typography>
                ) : null}
                {hasUnits ? (
                  <Typography component="li" variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                    {t('sites.affectUnitsWithRadios', 'Remove {{count}} unit(s) with attached radios', {
                      count: preflight.total_units,
                    })}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            {preflight.has_cellular && radioAction === 'purge' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t(
                  'sites.cellularSimDeactivateWarning',
                  'Some attached radios have cellular SIMs that will be deactivated. This cannot be undone.',
                )}
              </Alert>
            ) : null}

            {hasUnits ? (
              <FormControl component="fieldset" error={selectionError} sx={{ mt: 1, width: '100%' }}>
                <FormLabel component="legend">
                  {t('sites.whatShouldHappenToRadios', 'What should happen to the radios?')}
                </FormLabel>
                <RadioGroup
                  value={radioAction}
                  onChange={event => {
                    setRadioAction(event.target.value);
                    setSelectionError(false);
                  }}
                >
                  <FormControlLabel
                    value="inventory"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                          {t('sites.moveToPlantInventory', 'Move to plant inventory')}
                        </Typography>
                        <Typography component="span" variant="body2">
                          {' - '}
                          {t(
                            'sites.moveToPlantInventoryDescription',
                            'Units will be removed and radios can be re-installed later.',
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="purge"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                          {t('sites.retire', 'Retire')}
                        </Typography>
                        <Typography component="span" variant="body2">
                          {' - '}
                          {t(
                            'sites.retireDescriptionPlain',
                            'Deactivated and removed from this account. The device cannot be reactivated for future use.',
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
                {selectionError ? (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {t('sites.chooseRadioAction', 'Choose what should happen to the radios.')}
                  </Typography>
                ) : null}
              </FormControl>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={confirmDisabled}>
          {t('actions.deactivate', 'Deactivate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
